import type { ObjectListFeedItem, FeedCardProps } from "./feedTypes.js";
import "./ObjectListCard.css";

/**
 * Generic Object List Card Component
 * Displays a list of business objects as tiles/cards
 * Can be used for any metadata type: tenants, users, transports, etc.
 */
export function ObjectListCard({ item, onAction }: FeedCardProps<ObjectListFeedItem>) {
  const { items = [], total, objectType, displayFields, titleField, subtitleField, iconField } = item.config;

  // Auto-detect title field if not specified
  const detectedTitleField = titleField || 
    (items[0] ? Object.keys(items[0]).find(k => k.includes('name') || k.includes('Name') || k === 'displayName' || k === 'email') : undefined) ||
    'id';

  // Auto-detect subtitle field if not specified
  const detectedSubtitleField = subtitleField ||
    (items[0] ? Object.keys(items[0]).find(k => k.includes('description') || k.includes('type') || k.includes('Type')) : undefined);

  const renderFieldValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'number') return value.toLocaleString('de-DE');
    return String(value);
  };

  const getDisplayableFields = (obj: Record<string, unknown>): Array<{ key: string; value: unknown }> => {
    const excludeFields = ['_id', 'id', 'type', 'tenantId', 'createdAt', 'updatedAt', '__v', detectedTitleField, detectedSubtitleField, iconField];
    
    if (displayFields && displayFields.length > 0) {
      return displayFields
        .filter(field => !excludeFields.includes(field))
        .map(field => ({ key: field, value: obj[field] }));
    }

    return Object.entries(obj)
      .filter(([key]) => !excludeFields.includes(key))
      .slice(0, 5) // Max 5 additional fields
      .map(([key, value]) => ({ key, value }));
  };

  if (items.length === 0) {
    return (
      <div className="object-list-empty">
        <p>Keine {objectType || 'Objekte'} gefunden</p>
      </div>
    );
  }

  return (
    <div className="object-list-grid">
      {items.map((obj, index) => {
          const objectId = String(obj._id || obj.id || index);
          const title = renderFieldValue(obj[detectedTitleField]);
          const subtitle = detectedSubtitleField ? renderFieldValue(obj[detectedSubtitleField]) : undefined;
          const icon = iconField ? renderFieldValue(obj[iconField]) : undefined;
          const fields = getDisplayableFields(obj);

          const handleClick = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[ObjectListCard] Tile clicked:', title, 'Full object:', obj);
            
            if (onAction) {
              // Build a "show" command with the title as search term
              // This will query the database and show either list (multiple results) or detail view (single result)
              const searchTerm = title;
              const showCommand = `show ${searchTerm}`;
              
              console.log('[ObjectListCard] Sending show command:', showCommand);
              
              onAction({ 
                id: `show-${objectId}`,
                type: 'send-message',
                payload: { 
                  message: showCommand
                } 
              });
            } else {
              console.warn('[ObjectListCard] onAction is not defined');
            }
          };

          return (
            <div key={objectId} className="object-list-item" onClick={handleClick}>
              <div className="object-list-item-header">
                {icon && (
                  <div className="object-list-item-icon">
                    {icon}
                  </div>
                )}
                <div className="object-list-item-title-group">
                  <h3 className="object-list-item-title">{title}</h3>
                  {subtitle && (
                    <p className="object-list-item-subtitle">{subtitle}</p>
                  )}
                </div>
              </div>
              
              {fields.length > 0 && (
                <div className="object-list-item-fields">
                  {fields.map(({ key, value }) => (
                    <div key={key} className="object-list-item-field">
                      <span className="object-list-item-field-label">{key}:</span>
                      <span className="object-list-item-field-value">{renderFieldValue(value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
